import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

import CountDownTimer from '../util/timer';
import * as BECaller from '../util/apiCaller';
import { getJiraCloudSource } from '../util/dashboardUtils';
import { SourceDropdown } from './SourceDropdown';

import syncIcon from '../assets/images/sync-info.svg';

const MAX_RETRIES = 10;

const SHORT_UNITS = { minute: 'm', hour: 'h', day: 'd', month: 'mo', year: 'y' };
export const SOURCE_HINT = 'Click to see where the data comes from.';

export function shortAgo(date) {
  const match = moment(date).fromNow(true).match(/^(\d+|an?) (minute|hour|day|month|year)/);
  if (!match) return '<1m ago';
  return `${/^\d/.test(match[1]) ? match[1] : 1}${SHORT_UNITS[match[2]]} ago`;
}

export class NextSync extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      noJob: true,
      inProgress: false
    };
  }

  retries = 0;
  timer = { destroy: () => {} };
  timeout;

  componentDidMount() {
    if ((this.props.app === 'jira' || this.props.app === 'azure') && this.props.dashboard && this.props.dashboard._id) {
      this.init();
    }
  }

  componentDidUpdate(prevProps) {
    let prevDashboard = prevProps.dashboard || {};
    if (
      (this.props.app === 'jira' || this.props.app === 'azure') &&
      this.props.dashboard &&
      this.props.dashboard._id &&
      (prevDashboard._id != this.props.dashboard._id || prevProps.lastUpdated != this.props.lastUpdated)
    ) {
      this.init();
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      this.timer.destroy();
      !!this.timeout && clearTimeout(this.timeout);
    }
  }

  init() {
    if (this.timer) {
      this.timer.destroy();
      !!this.timeout && clearTimeout(this.timeout);
    }
    this.retries = 0;
    this.setState({ noJob: false, loading: true });
    this.getNextSyncTime();
  }

  getNextSyncTime() {
    BECaller.api(`dashboards/${this.props.dashboard._id}/nextsync`).then(data => {
      if (data.noJob || data.noMeta) {
        this.setState({ noJob: true });
        return false;
      }

      if (!data.running && !data.nextRunAt && this.retries < MAX_RETRIES) {
        this.retries++;
        this.timeout = setTimeout(() => this.getNextSyncTime(), 10000);
        return false;
      }

      if (data.running) {
        this.setState({ loading: false, inProgress: true });
        this.timeout = setTimeout(() => this.getNextSyncTime(), 5000);
      }

      if (!data.running && data.nextRunAt) {
        this.startTimer(moment(data.nextRunAt).diff(moment(), 'seconds'));
        this.setState({ loading: false, inProgress: false });
      }
    });
  }

  startTimer(duration) {
    this.timer = new CountDownTimer(duration);
    this.timer.onTick((min, sec) => {
      this.setState({
        minutes: Math.max(1, min + (sec > 0 ? 1 : 0))
      });
    });
    this.timer.onExpire(() => {
      this.setState({ inProgress: true });
      this.timeout = setTimeout(() => this.getNextSyncTime(), 1000);
    });
    this.timer.start();
  }

  getDashboardApp() {
    const { app, dashboard } = this.props;
    return (dashboard && dashboard.application) || app;
  }

  renderSyncLabel(title, value, short) {
    return (
      <React.Fragment>
        <span className='sync-full'>
          <span className='sync-title'>{title}</span>
          <span className='sync-value'>{value}</span>
        </span>
        {!!short && <span className='sync-short'>{short}</span>}
      </React.Fragment>
    );
  }

  renderSyncHint(text) {
    return <Tooltip id="tooltip-settings" className="tooltip_dark sync-tooltip text-align-left">{text}</Tooltip>;
  }

  renderSync(title, value, short, tooltip) {
    const { dashboard, user } = this.props;
    const label = this.renderSyncLabel(title, value, short);

    const jiraSource = this.getDashboardApp() === 'jira' && getJiraCloudSource(dashboard, user);
    if (jiraSource) {
      const hint = tooltip ? `${tooltip} ${SOURCE_HINT}` : SOURCE_HINT;
      return (
        <SourceDropdown key={dashboard._id} source={jiraSource} hint={this.renderSyncHint(hint)}>
          <img src={syncIcon} alt="#" />
          {label}
        </SourceDropdown>
      );
    }

    const sync = (
      <span className="next_sync">
        <img src={syncIcon} alt="#" />
        {label}
      </span>
    );
    if (!tooltip) {
      return sync;
    }
    return <OverlayTrigger placement="bottom" overlay={this.renderSyncHint(tooltip)}>{sync}</OverlayTrigger>;
  }

  render() {
    const { app, dashboard, lastUpdated } = this.props;
    const { noJob, loading, inProgress, minutes } = this.state;

    if (app === 'file' && lastUpdated) {
      const ago = moment(lastUpdated).fromNow();
      const short = shortAgo(lastUpdated);
      return this.renderSync(
        'Last file uploaded',
        ago,
        short,
        'Your data was last updated when this file was uploaded.'
      );
    }

    if (noJob && dashboard && (dashboard.lastWebhookReceived || dashboard.lastSynced || lastUpdated)) {
      const lastChange = Math.max(+new Date(dashboard.lastWebhookReceived || 0), +new Date(dashboard.lastSynced || 0), +new Date(lastUpdated || 0));
      const ago = moment(lastChange).fromNow();
      const short = shortAgo(lastChange);
      return this.renderSync(
        'Last change detected',
        ago,
        short,
        `Nave updates when your board changes. The last change was detected ${ago}.`
      );
    }

    if (loading || (app !== 'jira' && app !== 'azure')) {
      return <span className='next_sync none'></span>;
    }

    if (inProgress) {
      return this.renderSync('Syncing', 'Updating your data…', 'Syncing…');
    }

    const inMinutes = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    return this.renderSync(
      'Next update',
      `in ${inMinutes}`,
      `${minutes} min`,
      `Nave checks for new data every 30 minutes. Next update in ${inMinutes}.`,
    );
  }
}

export function mapStateToProps(state) {
  return {
    app: state.app.app,
    lastUpdated: state.app.lastUpdated,
    dashboard: state.dashboards.selected,
    user: state.auth.user
  };
}

export default connect(mapStateToProps)(NextSync);
